import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { Box } from '@mui/material';
import { validatePermissions } from 'lib/functions/utils';
import { AuthState } from 'services/redux/authSlice';
import { showAlert } from 'lib/functions/utils';
import AlertBanner from 'lib/designSystem/AlertBanner';
import CommitteeInfoCard from './CommitteeInfoCard';
import CommitteeTabs from './CommitteeTabs';
import CommitteeChangeDialog from './CommitteeChangeDialog';
import { Cancel } from '@mui/icons-material';
import { CommitteeService } from './types';

interface CommitteeProps {
  committeeService: CommitteeService;
}

const Committee = ({ committeeService }: CommitteeProps) => {
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [approvalsRequired, setApprovalsRequired] = useState<any[]>([]);
  const [approvals, setApprovals] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [canApprove, setCanApprove] = useState(false);
  const [comitee, setComitee] = useState<any>(null);
  const [table, setTable] = useState<any>('comitee');
  const [openCommitteeChange, setOpenCommitteeChange] = useState(false);

  const canEdit = committeeService.canEdit;
  const isRejected = committeeService.isRejected;

  const canChangeCommittee =
    canEdit && validatePermissions(user, 'comitee_change');
  const canDeleteApproval =
    canEdit && validatePermissions(user, 'comitee_delete_approval');

  const setUpApprovals = async () => {
    setLoading(true);
    try {
      const responseApprovals = await committeeService.getMissingApprovals(
        dispatch,
        committeeService.requestId,
      );
      setApprovalsRequired(responseApprovals.rolesToApprove);
      setApprovals(responseApprovals.approvals);
      setComitee(responseApprovals.comitee);

      if (
        responseApprovals.rolesToApprove.some(
          (role: any) => role.userCanApprove,
        )
      ) {
        setCanApprove(true);
      }

      if (isRejected) setCanApprove(false);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const handleApprove = async (reason: string) => {
    try {
      setLoading(true);
      await committeeService.approve(dispatch, committeeService.requestId, {
        reason,
      });
      showAlert(dispatch, 'success', 'Aprobación realizada exitosamente');
      await setUpApprovals();
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al realizar la aprobación');
      await setUpApprovals();
    }
    setLoading(false);
  };

  const handleReject = async (reason: string) => {
    try {
      setLoading(true);
      await committeeService.reject(dispatch, committeeService.requestId, {
        reason,
      });
      showAlert(dispatch, 'success', 'Rechazo realizado exitosamente');
      await setUpApprovals();
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al realizar el rechazo');
      await setUpApprovals();
    }
    setLoading(false);
  };

  const handleCommitteeChange = async (comiteeId: string) => {
    try {
      setLoading(true);
      await committeeService.updateCommittee(
        dispatch,
        committeeService.requestId,
        {
          comiteeId,
        },
      );
      showAlert(dispatch, 'success', 'Comité actualizado exitosamente');
      await setUpApprovals();
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al actualizar el comité');
      await setUpApprovals();
    }
    setLoading(false);
  };

  const handleDeleteApproval = async (approvalId: number) => {
    try {
      setLoading(true);
      await committeeService.deleteApproval(
        dispatch,
        committeeService.requestId,
        approvalId,
      );
      showAlert(dispatch, 'success', 'Aprobación eliminada exitosamente');
      await setUpApprovals();
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al eliminar la aprobación');
      await setUpApprovals();
    }
    setLoading(false);
  };

  const handleEditCommitteeClick = () => {
    setOpenCommitteeChange(true);
  };

  useEffect(() => {
    setUpApprovals();
  }, []);

  if (loading) return <CenteredCircularProgress type="dialog" />;

  return (
    <Box sx={{ my: 2 }}>
      {isRejected && !comitee && (
        <AlertBanner
          severity="error"
          icon={<Cancel sx={{ fontSize: '1.5rem', color: 'error.main' }} />}
          title="Estado rechazado"
          message="Este elemento ha sido rechazado por el comité. No se pueden realizar nuevas aprobaciones en este estado."
        />
      )}

      <CommitteeInfoCard
        comitee={comitee}
        onEditClick={canChangeCommittee ? handleEditCommitteeClick : undefined}
        canEdit={canChangeCommittee}
      />

      {comitee && (
        <CommitteeTabs
          table={table}
          setTable={setTable}
          approvalsRequired={approvalsRequired}
          approvals={approvals}
          handleApprove={handleApprove}
          handleReject={handleReject}
          canApprove={canApprove}
          canEdit={canEdit}
          onDeleteApproval={handleDeleteApproval}
          canDeleteApproval={canDeleteApproval}
        />
      )}

      {openCommitteeChange && (
        <CommitteeChangeDialog
          open={openCommitteeChange}
          setOpen={setOpenCommitteeChange}
          currentComitee={comitee}
          onSave={handleCommitteeChange}
          onCancel={() => setOpenCommitteeChange(false)}
        />
      )}
    </Box>
  );
};

export default Committee;
